import {
  AudioPresets,
  type RoomConnectOptions,
  type RoomOptions,
  ScreenSharePresets,
  VideoPreset,
  VideoPresets,
} from 'livekit-client'

import { canEncodeFullLadder, type DeviceClass, deviceClass } from './deviceClass'
import type { MeetingRole } from './types'

/**
 * How this client is configured to send and receive media.
 *
 * This file exists because livekit-client's defaults are tuned for a small
 * meeting between adults on good connections, and ours is a class of up to thirty
 * children on mid-range Android phones in Cairo. Verified against the installed
 * livekit-client (`roomOptionDefaults`, `publishDefaults`), the defaults are:
 *
 *   adaptiveStream: false      dynacast: false        autoSubscribe: true
 *   capture: 1280x720 @ 30fps  audioPreset: music     maxRetries: 1
 *
 * Left alone, a twenty-student class means every phone subscribes to every camera
 * at full quality, with no downgrade for a thumbnail-sized tile and no way for a
 * publisher to stop encoding layers nobody is watching. The result is not a
 * slightly worse picture — it is decoder overload, a starved uplink, and audio
 * breaking up. Audio breaking up is the class failing.
 *
 * The two that matter most:
 *
 * - `adaptiveStream` makes the SDK ask for the resolution a tile is actually
 *   displayed at, and pause tracks scrolled out of view.
 * - `dynacast` lets a publisher stop encoding layers nobody is subscribed to,
 *   which is uplink given back on the device that can least afford to waste it.
 */

/** A phone's own screen is small; a phone's pixel ratio is not. See below. */
const PIXEL_DENSITY = 1

/**
 * "No preference" must reach livekit-client as ABSENT, never as `''`.
 *
 * livekit-client's `createLocalTracks` upgrades any deviceId it finds as a
 * *string* to `{ exact: deviceId }` (its own comment: "if the user passes a
 * device id as a string, we default to exact match") and keeps an `{ ideal: … }`
 * copy to retry with. It then tries to fill a missing id via
 * `deviceId || 'default'` — but by that point `''` has already become the
 * OBJECT `{ exact: '' }`, which is truthy, so that rescue never fires.
 *
 * The prejoin's device selects start at `''` and most users never open them, so
 * the common path was: getUserMedia with `{ exact: '' }` → OverconstrainedError
 * → recursive retry with `{ ideal: '' }` → success on the default device. Two
 * device acquisitions per join instead of one, on Windows machines where
 * opening the audio stack is the slow part, plus a constraint error in the
 * console that makes every support session start by chasing a red herring.
 *
 * Omitting it entirely lets livekit substitute the plain string `'default'`,
 * which is an IDEAL constraint and — on Windows specifically — the right
 * semantics: playback and capture follow whatever the OS default device is,
 * rather than pinning a concrete endpoint that a driver may rename on wake.
 */
const deviceIdOrUndefined = (id?: string): string | undefined => (id ? id : undefined)

/**
 * the bottom rung of the screen-share simulcast ladder — ~360p @ 3fps.
 * Constructed, not borrowed: no bundled ScreenSharePresets value lands here
 * (the SDK's lowest is h720fps5 at ~800 kbps, far too high to be a floor).
 * Under real congestion this keeps shared text legible while bandwidth
 * estimation negotiates DOWN onto it, instead of starving a fixed resolution.
 */
const SCREEN_SHARE_FLOOR = new VideoPreset(640, 360, 150_000, 3)

export function roomOptionsFor(
  role: MeetingRole,
  devices: {
    videoDeviceId?: string
    audioDeviceId?: string
  },
  /** Injected for tests; the probed device class decides the student ladder () and the host capture (). */
  device: DeviceClass = deviceClass(),
): RoomOptions {
  // The teacher is the one every student is actually watching, so they get a
  // sharper capture. A student's tile is one of many and is usually small.
  //
  // The capture preset is ALSO the top layer the SDK will publish, but NOT by
  // the mechanism this comment used to describe. `computeVideoEncodings`
  // (verified against livekit-client 2.22.2) does not filter the ladder
  // per-layer at all. It reads AT MOST THE FIRST TWO configured presets and
  // then switches on the capture's longest edge:
  //
  //   longest edge ≥ 960 and a second preset exists → [low, mid, capture]
  //   longest edge ≥ 480                            → [low, capture]
  //   otherwise                                     → [capture]
  //
  // Two consequences worth knowing before touching either value below.
  // A THIRD entry in videoSimulcastLayers would be silently ignored. And the
  // student sits exactly on the cliff: h540 is 960×540, so its longest edge is
  // 960 — the threshold itself. Any drop in the student capture (h480, or the
  // 4:3 h540 at 720×540) silently collapses them from three layers to two.
  // With these values, the published sets are:
  //   Host:   [h180, h360]   weak host (keeps h360 there)
  //   Host:   [h180, h360, h540]   capable host
  //   Student: [h180, h360, h540]
  // The student's three-layer ladder is intentional for finer adaptive
  // quality: dynacast prunes layers nothing is subscribed to toward the
  // h180 baseline, so the cost of an unrequested mid layer is paid only
  // while someone is actually asking for it. See videoSimulcastLayers below:
  // CPU-weak student devices now publish the surgical 2-layer ladder ().
  //
  // the host capture CAP is now device-aware. It was 360p for every
  // host on purpose — 720p encoding + brand wall was the CPU tax on weak
  // Windows boxes, and the publish saver cannot shrink capture — but that
  // same blanket cap is why teachers on healthy hardware looked softer than
  // another calling app. A host whose device passes the same spare-capacity class the
  // student ladder and segmentation default use (canEncodeFullLadder,
  // deviceClass.ts) now captures at h540, which per the rules above lifts
  // their published set to three layers ([h180, h360, h540]) exactly like a
  // student's. Weak devices keep h360 and the two-layer ladder unchanged:
  // the fleet this cap was tuned for never sees a difference.
  const capture =
    role === 'Host'
      ? // h540 only where the device has spare capacity — the same
        // signal gates the student ladder on. Weak hosts keep h360.
        canEncodeFullLadder(device)
        ? VideoPresets.h540
        : VideoPresets.h360
      : VideoPresets.h540

  return {
    // pixelDensity 1, deliberately not 'screen'. 'screen' multiplies the
    // requested resolution by devicePixelRatio — on a cheap Android reporting
    // DPR 3, that asks for THREE times the pixels for the same small tile, which
    // is the exact opposite of what this device needs.
    adaptiveStream: { pixelDensity: PIXEL_DENSITY },
    dynacast: true,

    // OFF because the SDK's page-leave hook fires on `beforeunload` as well as
    // `pagehide` — and beforeunload fires when the tab-close CONFIRM APPEARS,
    // not when the user actually leaves. With the tab-close guard showing a
    // native "leave site?" prompt (useTabCloseGuard), the default would tear
    // the room down the moment the prompt opened: press Cancel, keep the tab,
    // lose the class anyway. The guard owns the real-exit disconnect instead,
    // on `pagehide` only, which never fires for a canceled prompt.
    //
    // What this flag does NOT buy us, so nobody has to rediscover it: the SDK
    // registers `freeze` → disconnect OUTSIDE this option's check (Room.ts,
    // livekit-client 2.22.2), so a browser that freezes a backgrounded tab
    // still tears the room down regardless of the setting. It is not reachable
    // from the tab-close prompt, which is what this flag was turned off for,
    // and Chrome exempts pages holding live WebRTC from freezing — so it is a
    // documented gap rather than an open bug. If children on Android ever
    // report being dropped after switching apps, start here.
    disconnectOnPageLeave: false,

    videoCaptureDefaults: {
      deviceId: deviceIdOrUndefined(devices.videoDeviceId),
      resolution: capture.resolution,
    },
    // audioOutput is NOT set here, deliberately. The chosen speaker is applied
    // in-room via switchActiveDevice (see useSpeakerChoice) because the stored id
    // has to be validated against the live device list first — which is async —
    // and because the room is entered by more paths than the prejoin: a reconnect
    // never passes through it, and a speaker choice that silently stopped
    // applying on reconnect would be worse than not having the feature.
    audioCaptureDefaults: {
      deviceId: deviceIdOrUndefined(devices.audioDeviceId),
      // Explicit rather than inherited: these three are what make a child in a
      // room with a fan and a sibling audible at all.
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },

    publishDefaults: {
      // The configured LOW ladder. computeVideoEncodings keeps every layer
      // whose dimensions are ≤ the capture top (size gate is INCLUSIVE) and
      // appends the capture itself as the top layer. Net published sets:
      //   Host:   [h180, h360]   (weak host, capture h360 — )
      //   Host:   [h180, h360, h540]   (capable host, capture h540 — )
      //   Student: [h180, h360, h540]
      // The student's third layer is the adaptive floor for a congested
      // subscriber. A capable host joins them at three layers (): their
      // tile is the one the whole class watches; a weak host stays two-layer
      // so the encoder is not also doing 540p on a machine that cannot.
      // (TODO(adaptive-02), executed): a CPU-weak student device —
      // the same GPU/cores/memory class that gates segmentation auto-default
      // (deviceClass, via canEncodeFullLadder) — publishes [h180] only, which
      // with the h540 capture yields the TODO's surgical 2-layer [h180, h540]
      // ladder (computeVideoEncodings: one configured preset + capture).
      // Capable devices keep all three layers. ponytail: deviceClass is a
      // heuristic stand-in for the runtime CPU-time signal the TODO wanted —
      // swap in a real CPU-pressure signal when one exists. The host ladder is untouched.
      videoSimulcastLayers:
        role === 'Host' || canEncodeFullLadder(device)
          ? [VideoPresets.h180, VideoPresets.h360]
          : [VideoPresets.h180],
      // 24 kbps speech instead of 48 kbps music. We carry a voice in a lesson,
      // never music, and this halves the audio uplink on the congested link where
      // audio is the thing that must survive.
      audioPreset: AudioPresets.speech,
      // Both are already defaults; named because they are load-bearing on a lossy
      // mobile network and must not be lost to a future default change. dtx stops
      // sending during silence; red adds audio redundancy against packet loss.
      dtx: true,
      red: true,
      // Lip sync over sharpness — for CAMERAS. A frozen frame of a teacher's
      // mouth mid-word is more disorienting for a child than a soft picture.
      // This default also REACHES THE SCREEN SHARE (livekit-client applies its
      // per-source 'maintain-resolution' share default only when nothing is
      // set), which is why every share call site must pass
      // SCREEN_SHARE_PUBLISH from screenShare.ts to claw sharpness back.
      degradationPreference: 'maintain-framerate',
      // Back to the SDK's own 1080p15 default — a BITRATE ceiling, not a size:
      // `.encoding` is {maxBitrate, maxFramerate} only. The capture was always
      // ~1080p; we were asking the encoder to carry it in 1.5 Mbps, which on
      // dense text spends itself on compression artifacts (and, with the
      // degradation preference bug above, on shrinking the picture). 2.5 Mbps
      // is the headroom that makes 1080p text actually legible. Only teachers on
      // desktops ever share, and 98% of classes are 1:1–1:5, so the extra
      // ~1 Mbps lands on the best-provisioned uplink in the room.
      screenShareEncoding: ScreenSharePresets.h1080fps15.encoding,
      // The layer a congested student falls DOWN to. Left alone the SDK
      // derives one half-resolution layer (~540p @ 15fps, ~625 kbps): soft
      // text, and it spends its budget on frames. 720p@5fps keeps the code
      // readable and lets the frame rate pay for it — a shared editor is
      // mostly still pixels. Unlike screenShareEncoding, these presets DO
      // carry dimensions, which is what makes the layer 720p.
      //
      // Ceiling check: 2.5 + 0.8 + 0.15 = ~3.45 Mbps if all layers ran flat out.
      // They do not — bandwidth estimation ramps the sender to what the uplink
      // actually has, and dynacast drops layers nobody is on entirely. Worth
      // watching on a weak teacher uplink all the same.
      //
      // the floor rung below. One fallback (720p@5fps) left a cliff —
      // under congestion BWE either fell the whole way (a big sudden drop) or
      // starved bitrate at a fixed resolution (blocky text). A ~360p @ 3fps
      // rung gives the estimator a real bottom to negotiate onto while staying
      // legible; it is constructed here because no bundled preset lands there
      // (the SDK's lowest is the 720p5 above). degradationPreference stays
      // 'maintain-resolution' — congestion-aware degradation is a separate,
      // later decision.
      screenShareSimulcastLayers: [ScreenSharePresets.h720fps5, SCREEN_SHARE_FLOOR],
    },

    // NOT set, on purpose:
    //
    // webAudioMix — would mix remote audio into one output instead of one <audio>
    //   element per participant, which is genuinely appealing at thirty people.
    //   But Web Audio needs an unlock gesture on mobile, and when it fails it
    //   fails SILENTLY. A silent lesson is the worst outcome we have, so this
    //   needs a deliberate device test before it goes anywhere near production.
    //
    // stopMicTrackOnMute — releases the mic while muted, which would turn the OS
    //   recording indicator off. Nice for a child at home, but re-acquiring the
    //   mic on unmute can fail on low-end Android, and a student who cannot
    //   unmute cannot answer a question.
  }
}

export function connectOptionsFor(): RoomConnectOptions {
  return {
    // The default is ONE retry. On an Egyptian mobile network a first attempt
    // failing is ordinary rather than exceptional, and the cost of retrying is a
    // few seconds against a child not getting into their lesson.
    maxRetries: 3,
  }
}
